import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Alert, Badge, Button, Card, Form, Spinner } from 'react-bootstrap';
import {
  DEFAULT_THRESHOLDS,
  WeatherThresholds,
  TemperatureCategory,
  WindCategory,
  PrecipitationCategory,
  categorizeTemperature,
  categorizeWind,
  categorizePrecipitation,
} from '../../services/weatherCategorization';
import {
  getCachedWeather,
  refreshWeatherCache,
} from '../../services/weatherCacheService';

/**
 * CloseSignOffWeatherPanel — a self-contained end-time-aware weather panel
 * for the operator's per-combo close sign-off (OperatorComboWorkFlow). It
 * mirrors the close-side block inside FieldModeCompletionForm but owns its
 * own state, draft persistence (keyed by scopeId), and onChange callout so
 * it can be dropped into the new operator combo work-flow without touching
 * the legacy completion form.
 *
 * Responsibilities:
 *   1. Fetch a cached weather reading on mount via `getCachedWeather`. If
 *      `endTime` is more than 30 minutes in the past *and* still within the
 *      48 h history window, fetch `/api/v1/tenants/me/weather/recent` and
 *      apply the row whose timestamp is closest to the operator's intended
 *      end time. Otherwise use the live cache.
 *   2. Auto-categorise the cached reading via `categorizeTemperature`,
 *      `categorizeWind`, `categorizePrecipitation` and pre-select the
 *      matching radio in each of the three groups. Restored draft selections
 *      win over the system value so an operator's previous override is never
 *      silently clobbered.
 *   3. Render a Verified (green) vs Override (amber) badge next to each
 *      group's legend, comparing the operator's selection to the system-
 *      derived category.
 *   4. Render a Refresh button that re-fetches cached weather and resets
 *      to the system-derived category for any radio that was not manually
 *      overridden.
 *   5. Render an optional Override comment field whenever any of the three
 *      categories differs from its system value. Min length 3 chars; the
 *      latest comment is surfaced on the snapshot as `overrideComment`.
 *   6. Persist the operator's draft (selections + comment) to localStorage
 *      keyed by `closeSignOffDraft:${scopeId}` so the operator can leave
 *      and return to the page without losing input. Distinct from the
 *      legacy `spray-completion-draft-${sessionId}` key so the two flows
 *      do not collide.
 *   7. Emit `onChange({ temperatureCategory, windCategory,
 *      precipitationCategory, systemReported?, overrideComment? })` whenever
 *      any selection (or the comment) changes.
 *
 * Track: multi_operator_spray_sessions / Phase 6 / Task 6.13.
 */

export interface CloseSignOffWeatherSnapshot {
  temperatureCategory: TemperatureCategory | '';
  windCategory: WindCategory | '';
  precipitationCategory: PrecipitationCategory | '';
  systemReported?: {
    temp?: number;
    windSpeed?: number;
    precipitation?: number;
  };
  overrideComment?: string;
}

export interface CloseSignOffWeatherPanelProps {
  /** Per-combo (or per-session) id; persists draft selections under this scope. */
  scopeId: string;
  /** ISO / datetime-local string for the operator's intended end time. */
  endTime: string;
  /** Tenant thresholds for category boundaries. Defaults to DEFAULT_THRESHOLDS. */
  thresholds?: WeatherThresholds;
  /** Called whenever any selection or the override comment changes. */
  onChange: (snapshot: CloseSignOffWeatherSnapshot) => void;
  /**
   * Compact Mode opt-in (F5). Adds `.ntr-fol-weather` to the panel root and
   * nothing else — every visual change lives in nutrition.css under that class.
   *
   * Deliberately className-only: a structural branch under `compact` is the
   * thing that would break Protection, and neither this panel's tests nor
   * FieldModePreFlightForm's / OperatorComboWorkFlow's would see it. With one
   * expression and one branch, "Protection renders byte-identically" is
   * provable rather than asserted.
   *
   * The .ntr-fol-* class appears inside components/spray/ on purpose: it is
   * applied ONLY when the foliar caller opts in, and that caller imports
   * nutrition.css.
   */
  compact?: boolean;
}

interface RawWeather {
  timestamp?: string;
  temp?: number;
  windSpeed?: number;
  precipitation?: number;
  condition?: string;
  humidity?: number;
  pressure?: number;
}

interface CloseSignOffDraft {
  temperatureCategory?: TemperatureCategory;
  windCategory?: WindCategory;
  precipitationCategory?: PrecipitationCategory;
  overrideComment?: string;
}

const DRAFT_STORAGE_PREFIX = 'closeSignOffDraft:';
const draftKey = (scopeId: string) => `${DRAFT_STORAGE_PREFIX}${scopeId}`;

// Hysteresis matching OpenSignOffWeatherPanel: an endTime within 30 min of
// "now" still uses the live cache path. Without this band the panel would
// flip to the historical branch on every keystroke as the operator nudged
// the End Time around.
const HISTORICAL_THRESHOLD_MS = 30 * 60 * 1000;
// 48 h matches WeatherAPI's history endpoint window and the backend's
// /me/weather/recent cap.
const HISTORY_WINDOW_MS = 48 * 60 * 60 * 1000;

const OVERRIDE_COMMENT_MIN_LENGTH = 3;

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readDraft(scopeId: string): CloseSignOffDraft | null {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(draftKey(scopeId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as CloseSignOffDraft;
  } catch {
    return null;
  }
}

function writeDraft(scopeId: string, draft: CloseSignOffDraft): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.setItem(draftKey(scopeId), JSON.stringify(draft));
  } catch {
    // ignore — losing draft persistence is preferable to crashing the form.
  }
}

interface CategoryRadioGroupProps<V extends string> {
  legend: string;
  groupName: string;
  testIdSuffix: string;
  options: { value: V; key: string }[];
  value: V | '';
  systemValue: V | null;
  onChange: (next: V) => void;
  t: (key: string) => string;
  verifiedLabel: string;
  overrideLabel: string;
}

function CategoryRadioGroup<V extends string>({
  legend,
  groupName,
  testIdSuffix,
  options,
  value,
  systemValue,
  onChange,
  t,
  verifiedLabel,
  overrideLabel,
}: CategoryRadioGroupProps<V>) {
  const verified = Boolean(systemValue && value && systemValue === value);
  const overridden = Boolean(systemValue && value && systemValue !== value);

  return (
    <fieldset className="mt-3" aria-label={legend}>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <legend className="form-label mb-0 fw-semibold">{legend}</legend>
        {verified && (
          <Badge bg="success" data-testid={`cs-weather-status-${testIdSuffix}`}>
            <i className="bi bi-check-circle-fill me-1" aria-hidden="true" />
            {verifiedLabel}
          </Badge>
        )}
        {overridden && (
          <Badge
            bg="warning"
            text="dark"
            data-testid={`cs-weather-status-${testIdSuffix}`}
          >
            <i
              className="bi bi-exclamation-triangle-fill me-1"
              aria-hidden="true"
            />
            {overrideLabel}
          </Badge>
        )}
      </div>
      <div className="d-flex flex-wrap gap-3 mt-2">
        {options.map(opt => {
          const optLabel = t(opt.key);
          // Compose the radio's accessible name as "${legend} ${optionLabel}"
          // so getByLabelText(/wind.*strong/i)-style queries land deterministically
          // and screen readers announce both the group and the value.
          const radioAriaLabel = `${legend} ${optLabel}`;
          return (
            <Form.Check
              key={opt.value}
              type="radio"
              id={`${groupName}-${opt.value}`}
              name={groupName}
              label={optLabel}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              aria-label={radioAriaLabel}
            />
          );
        })}
      </div>
    </fieldset>
  );
}

const CloseSignOffWeatherPanel: React.FC<CloseSignOffWeatherPanelProps> = ({
  scopeId,
  endTime,
  thresholds = DEFAULT_THRESHOLDS,
  onChange,
  compact = false,
}) => {
  const { t } = useTranslation();

  // Read any previously-saved selections synchronously on first render so
  // the radios reflect the operator's earlier choices without flickering
  // through the auto-pre-selected values when the cache lookup lands.
  const initialDraft = readDraft(scopeId);

  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState<RawWeather | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [temperatureCategory, setTemperatureCategory] =
    useState<TemperatureCategory | ''>(initialDraft?.temperatureCategory ?? '');
  const [windCategory, setWindCategory] = useState<WindCategory | ''>(
    initialDraft?.windCategory ?? ''
  );
  const [precipitationCategory, setPrecipitationCategory] =
    useState<PrecipitationCategory | ''>(
      initialDraft?.precipitationCategory ?? ''
    );
  const [overrideComment, setOverrideComment] = useState<string>(
    initialDraft?.overrideComment ?? ''
  );

  // Tracks which categories the operator has manually touched (or restored
  // from draft) so the auto-categorisation path does not clobber overrides.
  // Refresh only resets categories whose flag is still false.
  const userTouchedRef = useRef<{
    temperature: boolean;
    wind: boolean;
    precipitation: boolean;
  }>({
    temperature: Boolean(initialDraft?.temperatureCategory),
    wind: Boolean(initialDraft?.windCategory),
    precipitation: Boolean(initialDraft?.precipitationCategory),
  });

  const TEMP_OPTIONS = [
    { value: 'Cold' as TemperatureCategory, key: 'spray.openSignOff.weather.tempCold' },
    { value: 'Optimal' as TemperatureCategory, key: 'spray.openSignOff.weather.tempOptimal' },
    { value: 'Hot' as TemperatureCategory, key: 'spray.openSignOff.weather.tempHot' },
  ];
  const WIND_OPTIONS = [
    { value: 'Calm' as WindCategory, key: 'spray.openSignOff.weather.windCalm' },
    { value: 'Breeze' as WindCategory, key: 'spray.openSignOff.weather.windBreeze' },
    { value: 'Strong' as WindCategory, key: 'spray.openSignOff.weather.windStrong' },
  ];
  const PRECIP_OPTIONS = [
    { value: 'None' as PrecipitationCategory, key: 'spray.openSignOff.weather.precipNone' },
    { value: 'Light' as PrecipitationCategory, key: 'spray.openSignOff.weather.precipLight' },
    { value: 'Heavy' as PrecipitationCategory, key: 'spray.openSignOff.weather.precipHeavy' },
  ];

  // Fetch the source-of-truth reading. Choice of endpoint keys off how stale
  // the parent's endTime is:
  //   - empty / within 30 min of "now"   → live cache (getCachedWeather)
  //   - 30 min – 48 h ago                → GET /weather/recent, closest row
  //   - > 48 h ago                       → live cache (no historical data
  //                                        in window)
  const loadFromSource = useCallback(
    async (cancelled: { current: boolean }) => {
      const target = endTime ? Date.parse(endTime) : NaN;
      const ageMs = Number.isFinite(target) ? Date.now() - target : 0;
      const wantHistorical =
        Number.isFinite(target) &&
        ageMs > HISTORICAL_THRESHOLD_MS &&
        ageMs <= HISTORY_WINDOW_MS;

      try {
        if (wantHistorical) {
          const res = await axios.get<{ entries: RawWeather[] }>(
            '/api/v1/tenants/me/weather/recent',
            {
              params: { hours: 48 },
              withCredentials: true,
            }
          );
          if (cancelled.current) return;
          const entries = res?.data?.entries || [];
          if (entries.length === 0) {
            // Fall back to live cache if the historical lookup is empty.
            const cached = await getCachedWeather();
            if (cancelled.current) return;
            setRaw((cached as RawWeather | null) || null);
            return;
          }
          const closest = entries.reduce((best, cur) => {
            const bd = Math.abs(Date.parse(best.timestamp ?? '') - target);
            const cd = Math.abs(Date.parse(cur.timestamp ?? '') - target);
            return cd < bd ? cur : best;
          });
          setRaw(closest);
        } else {
          const cached = await getCachedWeather();
          if (cancelled.current) return;
          setRaw((cached as RawWeather | null) || null);
        }
      } catch {
        // Any failure → fall back to whatever the cache still holds.
        try {
          const cached = await getCachedWeather();
          if (cancelled.current) return;
          setRaw((cached as RawWeather | null) || null);
        } catch {
          // Cache itself unreachable → leave raw null; manual entry stays open.
        }
      }
    },
    [endTime]
  );

  useEffect(() => {
    const cancelled = { current: false };
    setLoading(true);
    loadFromSource(cancelled).finally(() => {
      if (cancelled.current) return;
      setLoading(false);
    });
    return () => {
      cancelled.current = true;
    };
  }, [loadFromSource]);

  // System-categorised values, derived from the raw reading. Used both for
  // badge logic and as the seed for un-touched radios.
  const systemTemperature: TemperatureCategory | null =
    raw && typeof raw.temp === 'number'
      ? categorizeTemperature(raw.temp, thresholds.temperature)
      : null;
  const systemWind: WindCategory | null =
    raw && typeof raw.windSpeed === 'number'
      ? categorizeWind(raw.windSpeed, thresholds.wind)
      : null;
  const systemPrecipitation: PrecipitationCategory | null =
    raw && typeof raw.precipitation === 'number'
      ? categorizePrecipitation(raw.precipitation, thresholds.precipitation)
      : null;

  // Auto-seed each radio whose value is still empty AND the operator hasn't
  // touched it. The draft (read synchronously) wins over the system reading.
  useEffect(() => {
    if (!raw) return;
    if (!userTouchedRef.current.temperature && !temperatureCategory && systemTemperature) {
      setTemperatureCategory(systemTemperature);
    }
    if (!userTouchedRef.current.wind && !windCategory && systemWind) {
      setWindCategory(systemWind);
    }
    if (
      !userTouchedRef.current.precipitation &&
      !precipitationCategory &&
      systemPrecipitation
    ) {
      setPrecipitationCategory(systemPrecipitation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, systemTemperature, systemWind, systemPrecipitation]);

  // Persist draft + emit onChange whenever any user-facing field changes.
  useEffect(() => {
    writeDraft(scopeId, {
      temperatureCategory: temperatureCategory || undefined,
      windCategory: windCategory || undefined,
      precipitationCategory: precipitationCategory || undefined,
      overrideComment: overrideComment || undefined,
    });
  }, [scopeId, temperatureCategory, windCategory, precipitationCategory, overrideComment]);

  useEffect(() => {
    const snapshot: CloseSignOffWeatherSnapshot = {
      temperatureCategory,
      windCategory,
      precipitationCategory,
    };
    if (
      raw &&
      (typeof raw.temp === 'number' ||
        typeof raw.windSpeed === 'number' ||
        typeof raw.precipitation === 'number')
    ) {
      snapshot.systemReported = {
        temp: raw.temp,
        windSpeed: raw.windSpeed,
        precipitation: raw.precipitation,
      };
    }
    if (overrideComment) snapshot.overrideComment = overrideComment;
    onChange(snapshot);
    // onChange is intentionally omitted from deps — parents typically pass an
    // inline arrow that would re-fire this effect on every render. The snapshot
    // value is the source of truth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, temperatureCategory, windCategory, precipitationCategory, overrideComment]);

  const handleTemperatureChange = (next: TemperatureCategory) => {
    userTouchedRef.current.temperature = true;
    setTemperatureCategory(next);
  };
  const handleWindChange = (next: WindCategory) => {
    userTouchedRef.current.wind = true;
    setWindCategory(next);
  };
  const handlePrecipitationChange = (next: PrecipitationCategory) => {
    userTouchedRef.current.precipitation = true;
    setPrecipitationCategory(next);
  };

  // Refresh: re-pull the cache (server-side row was already written by
  // whatever upstream flow captured it) and reset the radios that the operator
  // hasn't manually touched. Manual overrides survive a refresh.
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshWeatherCache();
      const cancelled = { current: false };
      await loadFromSource(cancelled);
      // Reset un-touched radios so the new system reading is reflected.
      if (!userTouchedRef.current.temperature) setTemperatureCategory('');
      if (!userTouchedRef.current.wind) setWindCategory('');
      if (!userTouchedRef.current.precipitation) setPrecipitationCategory('');
    } finally {
      setRefreshing(false);
    }
  };

  const isOverridden = (system: string | null, user: string) =>
    Boolean(system && user && system !== user);

  const anyOverridden =
    isOverridden(systemTemperature, temperatureCategory) ||
    isOverridden(systemWind, windCategory) ||
    isOverridden(systemPrecipitation, precipitationCategory);

  if (loading && !raw) {
    return (
      <Card className={compact ? 'mb-3 ntr-fol-weather' : 'mb-3'} data-testid="cs-weather-loading">
        <Card.Body className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" role="status" />
          <span>{t('spray.openSignOff.weather.loading')}</span>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={compact ? 'mb-3 ntr-fol-weather' : 'mb-3'} data-testid="cs-weather-panel">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h2 className="h5 mb-0">
            {t('spray.closeSignOff.weather.title')}
          </h2>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="cs-weather-refresh"
          >
            {refreshing ? (
              <>
                <Spinner animation="border" size="sm" role="status" className="me-1" />
                {t('spray.completionForm.weatherRefreshing')}
              </>
            ) : (
              t('spray.closeSignOff.weather.refreshButton')
            )}
          </Button>
        </div>

        {!raw && !loading && (
          <Alert variant="info" data-testid="cs-weather-no-reading" className="mt-3 mb-0">
            {t('spray.completionForm.weatherNoCache')}
          </Alert>
        )}

        {raw && (
          <div className="d-flex flex-wrap gap-3 small mt-2">
            <span data-testid="cs-weather-temp">
              <strong>{t('spray.openSignOff.weather.tempLabel')}:</strong>{' '}
              {typeof raw.temp === 'number' ? raw.temp : '—'} °C
            </span>
            <span data-testid="cs-weather-wind">
              <strong>{t('spray.openSignOff.weather.windLabel')}:</strong>{' '}
              {typeof raw.windSpeed === 'number' ? raw.windSpeed : '—'} kph
            </span>
            <span data-testid="cs-weather-precip">
              <strong>{t('spray.openSignOff.weather.precipLabel')}:</strong>{' '}
              {typeof raw.precipitation === 'number' ? raw.precipitation : '—'} mm
            </span>
          </div>
        )}

        <CategoryRadioGroup<TemperatureCategory>
          legend={t('spray.openSignOff.weather.tempLegend')}
          groupName="cs-temperature"
          testIdSuffix="temperature"
          options={TEMP_OPTIONS}
          value={temperatureCategory}
          systemValue={systemTemperature}
          onChange={handleTemperatureChange}
          t={t}
          verifiedLabel={t('spray.closeSignOff.weather.verifiedBadge')}
          overrideLabel={t('spray.closeSignOff.weather.overrideBadge')}
        />
        <CategoryRadioGroup<WindCategory>
          legend={t('spray.openSignOff.weather.windLegend')}
          groupName="cs-wind"
          testIdSuffix="wind"
          options={WIND_OPTIONS}
          value={windCategory}
          systemValue={systemWind}
          onChange={handleWindChange}
          t={t}
          verifiedLabel={t('spray.closeSignOff.weather.verifiedBadge')}
          overrideLabel={t('spray.closeSignOff.weather.overrideBadge')}
        />
        <CategoryRadioGroup<PrecipitationCategory>
          legend={t('spray.openSignOff.weather.precipLegend')}
          groupName="cs-precipitation"
          testIdSuffix="precipitation"
          options={PRECIP_OPTIONS}
          value={precipitationCategory}
          systemValue={systemPrecipitation}
          onChange={handlePrecipitationChange}
          t={t}
          verifiedLabel={t('spray.closeSignOff.weather.verifiedBadge')}
          overrideLabel={t('spray.closeSignOff.weather.overrideBadge')}
        />

        {anyOverridden && (
          <Form.Group className="mt-3" controlId="cs-weather-override-comment">
            <Form.Label className="fw-semibold">
              {t('spray.closeSignOff.weather.overrideCommentLabel')}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={overrideComment}
              onChange={e => setOverrideComment(e.target.value)}
              placeholder={t('spray.completionForm.overrideCommentPlaceholder')}
              data-testid="cs-weather-override-comment"
              isInvalid={
                overrideComment.length > 0 &&
                overrideComment.trim().length < OVERRIDE_COMMENT_MIN_LENGTH
              }
            />
          </Form.Group>
        )}
      </Card.Body>
    </Card>
  );
};

export default CloseSignOffWeatherPanel;
